/**
 * Schedule View — appointment status buckets.
 *
 * ⛔ KEEP THIS FILE IMPORT-FREE (the node-tests execute it verbatim).
 *
 * ── THE PROBLEM THIS SOLVES ──────────────────────────────────────────────────────────────────
 * Open Dental lets every practice invent its own appointment-confirmation statuses. One office has
 * "Unconfirmed / Confirmed / Left Msg"; another has "Not Called / Called / Texted / Teamio
 * Confirmed / In Room". The design, reasonably, drew four colours.
 *
 * So Schedule View shows the office's REAL status name on the block — that is the word they know —
 * and separately rolls each status into one of four BUCKETS that drive the dot colour, the Status
 * filter and the Status dropdown counts. The mapping is per site, stored in the settings blob, and
 * auto-guessed from the status name until somebody adjusts it.
 *
 * ── WHY THE CHAIR-TIME STAMPS WIN ────────────────────────────────────────────────────────────
 * "Arrived" and "Completed" are facts Open Dental already records as timestamps, and as an
 * AptStatus. A confirmation status cannot contradict those: a patient who is sitting in the chair
 * is Arrived even if their confirmation still says "Left Msg". So the stamps are checked first and
 * the name-based mapping only decides confirmed-vs-unconfirmed.
 */

export const STATUS_BUCKETS = ['confirmed', 'unconfirmed', 'arrived', 'completed'];

/** OD AptStatus values this page draws. */
export const APT_STATUS_SCHEDULED = 1;
export const APT_STATUS_COMPLETE = 2;
export const APT_STATUS_BROKEN = 5;

/**
 * Is this Open Dental datetime actually set?
 *
 * OD writes a sentinel rather than NULL for "never happened" — '0001-01-01' from the .NET DateTime
 * minimum, and '1901-01-01' from older MySQL zero-date handling. Both are truthy strings, so a
 * plain `if (appt.DateTimeArrived)` marks EVERY appointment as arrived. The same sentinels are
 * filtered explicitly in main-service/sql/patient-profile.js.
 *
 * ⛔ THERE IS A SECOND SENTINEL, and it is the dangerous one. Verified on the dev mirror
 * 2026-09-20 against real rows:
 *
 *     AptNum 34924 (June 2026)      arrived/seated/dismissed = '0001-01-01 00:00:00'
 *     AptNum 35010 (September 2026) arrived/seated/dismissed = '2026-09-15 00:00:00'  ← the
 *                                   appointment's OWN DATE at midnight
 *
 * So recent rows carry a plausible-looking date for something that never happened. Accepting it
 * marks every appointment DISMISSED, which resolves the entire day to "completed" — a schedule
 * that is uniformly grey by mid-morning, and "everything is done" is not a claim anyone would
 * think to question. This was caught only by pointing the deployed endpoint at a real database;
 * it passed every unit test, because the fixtures only knew about the epoch sentinel.
 *
 * The rule that covers both: a chair time is a moment during a WORKING DAY. Exactly midnight is
 * never one, and a value with no time component at all is not a moment. If that is ever wrong,
 * it is wrong in the recoverable direction — the appointment falls back to its confirmation
 * status, which prompts a phone call rather than suppressing one.
 */
export function hasOdDateTime(value) {
  if (!value) return false;
  const s = String(value).trim();
  if (s === '') return false;

  if (s.startsWith('0001-01-01')) return false;
  if (s.startsWith('1901-01-01')) return false;
  if (s.startsWith('0000-00-00')) return false;

  const time = s.split(/[ T]/)[1];
  if (!time) return false;                          // a date with no time is not a moment
  if (/^00:00(:00)?(\.0+)?$/.test(time)) return false; // exactly midnight — see above

  return true;
}

/** Is this appointment broken? A flag, not a bucket — a broken block still shows its status. */
export function isBroken(appt) {
  return !!appt && Number(appt.AptStatus) === APT_STATUS_BROKEN;
}

/**
 * Guess a bucket from the office's own wording.
 *
 * Only ever a starting point: the office can override any status in the settings popover. The
 * negative check runs FIRST because "Unconfirmed" contains "confirm", and getting that backwards
 * would paint every unconfirmed appointment green — the single most misleading thing this page
 * could do, since "everyone is confirmed" is exactly what nobody would question.
 */
export function guessBucket(itemName) {
  const name = String(itemName || '').toLowerCase().trim();
  if (name === '') return 'unconfirmed';

  if (/\bun-?confirm/.test(name) || /\bnot\s+confirm/.test(name) || /\bno\s+confirm/.test(name)) {
    return 'unconfirmed';
  }
  if (/complet|dismiss|checked\s*out|check-?out|finished/.test(name)) return 'completed';
  if (/arriv|in\s*room|seated|waiting|checked\s*in|check-?in|here\b/.test(name)) return 'arrived';
  if (/confirm/.test(name)) return 'confirmed';
  return 'unconfirmed';
}

/**
 * The effective DefNum -> bucket map: the office's stored overrides on top of the guesses.
 *
 * @param {Array}  confirmationStatuses  definition rows from /schedule-view/day
 * @param {Object} storedBuckets         settings.confirmationBuckets (may be empty)
 */
export function buildBucketMap(confirmationStatuses, storedBuckets) {
  const map = {};
  (confirmationStatuses || []).forEach((def) => {
    if (!def || def.DefNum === undefined || def.DefNum === null) return;
    map[String(def.DefNum)] = guessBucket(def.ItemName);
  });
  if (storedBuckets && typeof storedBuckets === 'object') {
    Object.keys(storedBuckets).forEach((defNum) => {
      if (STATUS_BUCKETS.indexOf(storedBuckets[defNum]) !== -1) {
        map[String(defNum)] = storedBuckets[defNum];
      }
    });
  }
  return map;
}

/**
 * The bucket for one appointment.
 *
 * Order matters and is the whole design:
 *   1. Completed — OD says so, either by AptStatus or by a dismissal time.
 *   2. Arrived   — the patient is physically here (seated, or checked in).
 *   3. Otherwise the office's confirmation status decides confirmed vs unconfirmed.
 * A broken appointment keeps whatever bucket it had; `isBroken()` is drawn on top of it.
 */
export function resolveBucket(appt, bucketMap) {
  if (!appt) return 'unconfirmed';

  if (Number(appt.AptStatus) === APT_STATUS_COMPLETE) return 'completed';
  if (hasOdDateTime(appt.DateTimeDismissed)) return 'completed';
  if (hasOdDateTime(appt.DateTimeSeated) || hasOdDateTime(appt.DateTimeArrived)) return 'arrived';

  const key = String(appt.Confirmed);
  if (bucketMap && Object.prototype.hasOwnProperty.call(bucketMap, key)) return bucketMap[key];
  return guessBucket(appt.ConfirmedName);
}

/**
 * The label shown on the block: the office's own status name.
 *
 * Falls back to the bucket's generic word only when OD gave us nothing — an appointment with no
 * confirmation status at all is genuinely "Unconfirmed", and a blank label there reads as a
 * rendering bug rather than as information.
 */
export function statusLabel(appt, bucket) {
  const name = appt && appt.ConfirmedName;
  if (name && String(name).trim() !== '') return String(name).trim();
  const fallback = {
    confirmed: 'Confirmed',
    unconfirmed: 'Unconfirmed',
    arrived: 'Arrived',
    completed: 'Completed',
  };
  return fallback[bucket] || 'Unconfirmed';
}
